import { useEffect, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { Barcode, PencilLine, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { FormField } from '@/components/ui/FormField';
import { StarRating } from '@/components/ui/StarRating';
import { BookCover } from '@/components/books/BookCover';
import { loggedBooksService } from '@/services/logged-books.service';
import { ApiError } from '@/services/http';
import { cn } from '@/lib/cn';
import type { LoggedBookView } from '@/types/api';

/**
 * LogBookDialog — add or edit a Reading Log entry (a book not in the Ollie
 * Reads catalog). Adding opens on a two-way picker: "Enter ISBN" (auto-fills
 * title/author/cover from Google Books → Open Library) or "Enter manually".
 * Both paths converge on the same editable form, which also lets the reader
 * upload their own cover. Editing jumps straight to the form.
 */
export interface LogBookDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  readerId: string;
  /** When set, the dialog edits this entry instead of creating a new one. */
  editing?: LoggedBookView | null;
  onSaved: (book: LoggedBookView) => void;
}

const ACCEPTED_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
const MAX_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB — mirrors the admin cover uploader

type Step = 'choose' | 'isbn' | 'form';

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function LogBookDialog({
  open,
  onOpenChange,
  readerId,
  editing,
  onSaved,
}: LogBookDialogProps) {
  const [step, setStep] = useState<Step>('choose');

  // Form fields
  const [title, setTitle] = useState('');
  const [author, setAuthor] = useState('');
  const [isbn, setIsbn] = useState('');
  // Page count kept as the raw input string; parsed to a number on save.
  const [pageCount, setPageCount] = useState('');
  const [coverUrl, setCoverUrl] = useState<string | null>(null);
  const [rating, setRating] = useState<number | null>(null);
  const [note, setNote] = useState('');
  const [finishedOn, setFinishedOn] = useState<string>('');

  // ISBN step
  const [isbnInput, setIsbnInput] = useState('');
  const [looking, setLooking] = useState(false);

  // Cover upload + save
  const [uploadingCover, setUploadingCover] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Reset all state whenever the dialog opens, seeded from `editing` (edit
  // mode) or blank (create mode).
  useEffect(() => {
    if (!open) return;
    if (editing) {
      setStep('form');
      setTitle(editing.title);
      setAuthor(editing.author ?? '');
      setIsbn(editing.isbn ?? '');
      setPageCount(editing.pageCount != null ? String(editing.pageCount) : '');
      setCoverUrl(editing.coverUrl);
      setRating(editing.rating);
      setNote(editing.note ?? '');
      setFinishedOn(editing.finishedOn ?? todayIso());
    } else {
      setStep('choose');
      setTitle('');
      setAuthor('');
      setIsbn('');
      setPageCount('');
      setCoverUrl(null);
      setRating(null);
      setNote('');
      setFinishedOn(todayIso());
    }
    setIsbnInput('');
    setLooking(false);
    setUploadingCover(false);
    setSaving(false);
  }, [open, editing]);

  const handleLookup = async () => {
    const value = isbnInput.trim();
    if (!value) {
      toast.error('Enter an ISBN to look up.');
      return;
    }
    setLooking(true);
    try {
      const result = await loggedBooksService.lookup(value);
      setTitle(result.title);
      setAuthor(result.author ?? '');
      setIsbn(result.isbn);
      setPageCount(result.pageCount != null ? String(result.pageCount) : '');
      setCoverUrl(result.coverUrl);
      setStep('form');
      toast.success('Found it — review the details below.');
    } catch (err) {
      // Not found (404) or provider hiccup → fall through to manual entry with
      // the typed ISBN preserved so a save still records it.
      setIsbn(value);
      setStep('form');
      if (err instanceof ApiError && err.status === 404) {
        toast.message("Couldn't find that ISBN — add the details yourself.");
      } else {
        toast.error(err instanceof ApiError ? err.message : 'Lookup failed — enter it manually.');
      }
    } finally {
      setLooking(false);
    }
  };

  const handlePickCover = () => {
    if (uploadingCover) return;
    fileInputRef.current?.click();
  };

  const handleCoverFile = async (file: File) => {
    if (!ACCEPTED_TYPES.includes(file.type)) {
      toast.error('Cover must be a PNG, JPEG, WEBP, or GIF image.');
      return;
    }
    if (file.size > MAX_SIZE_BYTES) {
      toast.error('Cover files must be 5MB or smaller.');
      return;
    }
    setUploadingCover(true);
    try {
      const { coverUrl: uploaded } = await loggedBooksService.uploadCover(file);
      setCoverUrl(uploaded);
      toast.success('Cover added.');
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not upload the cover.');
    } finally {
      setUploadingCover(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      toast.error('A title is required.');
      return;
    }
    setSaving(true);
    const trimmedPages = pageCount.trim();
    const parsedPages =
      /^\d+$/.test(trimmedPages) && Number(trimmedPages) > 0 ? Number(trimmedPages) : null;
    const payload = {
      title: trimmedTitle,
      author: author.trim() || null,
      isbn: isbn.trim() || null,
      pageCount: parsedPages,
      coverUrl,
      rating,
      note: note.trim() || null,
      finishedOn: finishedOn || null,
    };
    try {
      const saved = editing
        ? await loggedBooksService.update(editing.id, payload)
        : await loggedBooksService.create({ readerId, ...payload });
      onSaved(saved);
      toast.success(editing ? 'Reading Log updated.' : `Added “${saved.title}” to the Reading Log.`);
      onOpenChange(false);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not save this book.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-or-slate-900/55 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 max-h-[calc(100vh-32px)] w-[calc(100vw-32px)] max-w-lg -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-2xl border border-or-slate-100 bg-white p-6 shadow-or-pop focus:outline-none">
          <div className="flex items-start justify-between gap-4">
            <Dialog.Title className="font-display text-[20px] font-bold text-or-slate-900">
              {editing ? 'Edit Reading Log entry' : 'Log a book'}
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                type="button"
                aria-label="Close"
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-or-slate-400 transition hover:bg-or-slate-100 hover:text-or-slate-600"
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            </Dialog.Close>
          </div>

          {step === 'choose' ? (
            <ChooseStep onIsbn={() => setStep('isbn')} onManual={() => setStep('form')} />
          ) : step === 'isbn' ? (
            <IsbnStep
              value={isbnInput}
              onChange={setIsbnInput}
              onLookup={() => void handleLookup()}
              looking={looking}
              onBack={() => setStep('choose')}
              onManual={() => setStep('form')}
            />
          ) : (
            <div className="mt-5 flex flex-col gap-4">
              <div className="flex gap-4">
                <div className="shrink-0">
                  <BookCover
                    title={title || 'Untitled'}
                    author={author}
                    src={coverUrl}
                    seed={editing?.id ?? title}
                    w={104}
                    h={150}
                    className="h-[150px] w-[104px]"
                  />
                  <div className="mt-2 flex flex-col gap-1">
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={handlePickCover}
                      disabled={uploadingCover}
                    >
                      {uploadingCover ? 'Uploading…' : coverUrl ? 'Replace cover' : 'Upload cover'}
                    </Button>
                    {coverUrl ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setCoverUrl(null)}
                        disabled={uploadingCover}
                      >
                        Remove cover
                      </Button>
                    ) : null}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPTED_TYPES.join(',')}
                    className="hidden"
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      if (file) void handleCoverFile(file);
                    }}
                  />
                </div>

                <div className="flex min-w-0 flex-1 flex-col gap-3">
                  <FormField
                    label="Title"
                    required
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Book title"
                    maxLength={300}
                  />
                  <FormField
                    label="Author"
                    value={author}
                    onChange={(e) => setAuthor(e.target.value)}
                    placeholder="Author (optional)"
                    maxLength={200}
                  />
                  <FormField
                    label="Pages"
                    type="number"
                    inputMode="numeric"
                    min={1}
                    max={100000}
                    value={pageCount}
                    onChange={(e) => setPageCount(e.target.value)}
                    placeholder="Page count (optional)"
                  />
                  <FormField
                    label="Date finished"
                    type="date"
                    value={finishedOn}
                    max={todayIso()}
                    onChange={(e) => setFinishedOn(e.target.value)}
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <span className="or-field-label text-[11px] font-semibold uppercase tracking-wider text-or-slate-600">
                  Your rating
                </span>
                <div className="flex items-center gap-3">
                  <StarRating value={rating ?? 0} onChange={setRating} size="lg" />
                  {rating ? (
                    <button
                      type="button"
                      onClick={() => setRating(null)}
                      className="text-[12px] font-medium text-or-slate-400 underline-offset-2 hover:text-or-slate-600 hover:underline"
                    >
                      Clear
                    </button>
                  ) : null}
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label
                  htmlFor="logged-book-note"
                  className="or-field-label text-[11px] font-semibold uppercase tracking-wider text-or-slate-600"
                >
                  Review / notes
                </label>
                <textarea
                  id="logged-book-note"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={3}
                  maxLength={2000}
                  placeholder="What did they think? (optional)"
                  className="rounded-xl border border-or-slate-200 bg-white px-3 py-2 text-[14px] text-or-slate-800 shadow-sm placeholder:text-or-slate-400 focus:border-or-orange-300 focus:outline-none focus:ring-2 focus:ring-or-orange-200/60"
                />
              </div>

              <div className="mt-1 flex items-center justify-between gap-2">
                {!editing ? (
                  <Button variant="ghost" size="md" onClick={() => setStep('choose')} disabled={saving}>
                    Back
                  </Button>
                ) : (
                  <span />
                )}
                <Button variant="primary" size="md" onClick={() => void handleSave()} disabled={saving}>
                  {saving ? 'Saving…' : editing ? 'Save changes' : 'Add to Reading Log'}
                </Button>
              </div>
            </div>
          )}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function ChooseStep({ onIsbn, onManual }: { onIsbn: () => void; onManual: () => void }) {
  return (
    <div className="mt-5 flex flex-col gap-3">
      <p className="text-[13px] leading-relaxed text-or-slate-600">
        Add a finished book that isn&rsquo;t in our library so it lands on the Bookshelf.
      </p>
      <button
        type="button"
        onClick={onIsbn}
        className="flex items-center gap-3 rounded-2xl border border-or-slate-200 bg-white px-4 py-4 text-left shadow-sm transition hover:border-or-orange-300 hover:bg-or-orange-50/40"
      >
        <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-or-orange-100 text-or-orange-700">
          <Barcode className="h-5 w-5" aria-hidden />
        </span>
        <span className="min-w-0">
          <span className="block font-display text-[15px] font-bold text-or-slate-900">
            Enter ISBN
          </span>
          <span className="block text-[12px] text-or-slate-500">
            We&rsquo;ll fill in the title, author, and cover automatically.
          </span>
        </span>
      </button>
      <button
        type="button"
        onClick={onManual}
        className="flex items-center gap-3 rounded-2xl border border-or-slate-200 bg-white px-4 py-4 text-left shadow-sm transition hover:border-or-orange-300 hover:bg-or-orange-50/40"
      >
        <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-or-slate-100 text-or-slate-600">
          <PencilLine className="h-5 w-5" aria-hidden />
        </span>
        <span className="min-w-0">
          <span className="block font-display text-[15px] font-bold text-or-slate-900">
            Enter manually
          </span>
          <span className="block text-[12px] text-or-slate-500">
            Type the details in yourself and optionally upload a cover.
          </span>
        </span>
      </button>
    </div>
  );
}

function IsbnStep({
  value,
  onChange,
  onLookup,
  looking,
  onBack,
  onManual,
}: {
  value: string;
  onChange: (v: string) => void;
  onLookup: () => void;
  looking: boolean;
  onBack: () => void;
  onManual: () => void;
}) {
  return (
    <div className="mt-5 flex flex-col gap-4">
      <FormField
        label="ISBN"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !looking) {
            e.preventDefault();
            onLookup();
          }
        }}
        placeholder="e.g. 978-0763665630"
        help="Found on the back cover or copyright page. ISBN-10 or ISBN-13."
        inputMode="numeric"
        autoFocus
      />
      <div className="flex items-center justify-between gap-2">
        <Button variant="ghost" size="md" onClick={onBack} disabled={looking}>
          Back
        </Button>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="md" onClick={onManual} disabled={looking}>
            Enter manually
          </Button>
          <Button variant="primary" size="md" onClick={onLookup} disabled={looking}>
            <Search className={cn('mr-1.5 h-4 w-4', looking && 'animate-pulse')} aria-hidden />
            {looking ? 'Looking…' : 'Look up'}
          </Button>
        </div>
      </div>
    </div>
  );
}
